import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  normalizeRole, 
  canAccessStaffPortal, 
  canAccessClientPortal,
  getRoleDisplayName,
  getEmergencyAccess,
  USER_ROLES 
} from '../../utils/roleMapping';

const StaffAccessControl = ({ children }) => {
  const { user } = useAuth();
  
  // Handle missing user
  if (!user) {
    console.warn('🚨 StaffAccessControl: No user found, redirecting to login');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🔐</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Authentication Required
          </h1>
          <p className="text-gray-600 mb-4">
            Please log in to access this portal.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }
  
  // Normalize role and check access
  const normalizedRole = normalizeRole(user.role);
  const emergencyAccess = getEmergencyAccess(user.role);
  
  // Check if user has proper role for staff access
  if (!canAccessStaffPortal(normalizedRole)) {
    // Determine appropriate redirect
    let redirectUrl = '/portal';
    let portalName = 'Client Portal';
    
    if (canAccessClientPortal(normalizedRole)) {
      redirectUrl = '/portal';
      portalName = 'Client Portal';
    } else {
      // Emergency fallback - always allow client portal access
      redirectUrl = '/portal';
      portalName = 'Client Portal';
      console.warn('🚨 StaffAccessControl: Emergency fallback activated for user:', user.email, 'with role:', user.role);
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the Staff Portal.
          </p>
          <p className="text-gray-500 mb-6">
            Based on your role ({getRoleDisplayName(normalizedRole)}), you should access the {portalName}.
          </p>
          
          {emergencyAccess.isEmergencyFallback && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Your role ({user.role}) has been automatically mapped to Client Portal access.
              </p>
            </div>
          )}
          
          <div className="space-y-3">
            <button
              onClick={() => window.location.href = redirectUrl}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
            >
              Go to {portalName}
            </button>
            <button
              onClick={() => window.history.back()}
              className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors duration-200 font-medium"
            >
              Go Back
            </button>
          </div>
          
          {/* Additional help for debugging */}
          <div className="mt-6 text-xs text-gray-400">
            <p>User ID: {user.id}</p>
            <p>Email: {user.email}</p>
            <p>Original Role: {user.role}</p>
            <p>Normalized Role: {normalizedRole}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // User has proper access - render children
  return children;
};

export default StaffAccessControl;
